import {
  CheckOutlined,
  CloseOutlined,
  ButtonDS,
  ModalDS,
  Spacer,
  TextCapitalized,
} from "@qivia/ui";
import styled from "styled-components";
import { RelatedDriverRequestsDisplayed } from "./driversApi";
import { typographies } from "@qivia/ui/src/styles/figmaTypographies";
import { colors } from "@qivia/ui/src/styles/figmaColors";
import { useTranslation } from "react-i18next";
import "../../i18n/i18n";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { updateRelatedDriverRequestAsync } from "./driversSlice";
import { selectAdminUser } from "../homeSlice";
import { useCallback } from "react";

type ModalStateProps = {
  isVisible: boolean;
  action: "ACCEPTED" | "REJECTED" | null;
  rowData: RelatedDriverRequestsDisplayed | null;
};

export const AssociateRelatedDriversModal = ({
  modalState,
  setModalState,
}: {
  modalState: ModalStateProps;
  setModalState: (modalState: ModalStateProps) => void;
}) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const adminUser = useAppSelector(selectAdminUser);

  const handleCloseModal = useCallback(() => {
    setModalState({
      isVisible: false,
      action: null,
      rowData: null,
    });
  }, [setModalState]);

  const handleConfirm = useCallback(() => {
    if (modalState.rowData?.uuid && modalState.action && adminUser) {
      void dispatch(
        updateRelatedDriverRequestAsync({
          uuid: modalState.rowData?.uuid,
          status: modalState.action,
          reviewer: adminUser.email,
        }),
      );
    }
    handleCloseModal();
  }, [
    dispatch,
    modalState.rowData?.uuid,
    modalState.action,
    adminUser,
    handleCloseModal,
  ]);

  return (
    <ModalDS
      visible={modalState.isVisible}
      onClose={handleCloseModal}
      width={40}
    >
      <StyledModalContent>
        <StyledTitle>
          <TextCapitalized>
            {t(`relatedDriverRequests.modal.title.${modalState.action}`)}
          </TextCapitalized>
          <Spacer x={1} />
          {modalState.action === "ACCEPTED" ? (
            <StyledIcon $backgroundColor="colors/system/success/success_normal">
              <CheckOutlined />
            </StyledIcon>
          ) : (
            <StyledIcon $backgroundColor="colors/system/error/error_normal">
              <CloseOutlined />
            </StyledIcon>
          )}
        </StyledTitle>
        <Spacer y={2} />
        <StyledSubTitle>
          <TextCapitalized>
            {t(`relatedDriverRequests.modal.text.${modalState.action}`)}
          </TextCapitalized>
        </StyledSubTitle>
        <Spacer y={3} />
        {modalState.rowData && (
          <>
            <StyledText>
              <TextCapitalized>
                {t("relatedDriverRequests.modal.oldDriver")}:{" "}
                {modalState.rowData.oldDriverName}
              </TextCapitalized>
            </StyledText>
            <Spacer y={1} />
            <StyledText>
              <TextCapitalized>
                {t("relatedDriverRequests.modal.newDriver")}:{" "}
                {modalState.rowData.newDriverName}
              </TextCapitalized>
            </StyledText>
          </>
        )}
        <Spacer y={3} />
        <StyledFlex>
          <ButtonDS
            text={t("relatedDriverRequests.modal.cancel")}
            format="fill"
            buttonType="secondary"
            sizeButton="M"
            onClick={handleCloseModal}
          />
          <Spacer x={1} />
          <ButtonDS
            text={t("relatedDriverRequests.modal.confirm")}
            format="fill"
            buttonType={modalState.action === "ACCEPTED" ? "primary" : "danger"}
            sizeButton="M"
            onClick={handleConfirm}
          />
        </StyledFlex>
        <Spacer y={1.5} />
      </StyledModalContent>
    </ModalDS>
  );
};

const StyledModalContent = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  align-items: center;
`;

const StyledTitle = styled.div`
  ${typographies["Header/H1"]}
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const StyledSubTitle = styled.div`
  ${typographies["Header/H2"]}
  text-align: center;
`;

const StyledText = styled.div`
  ${typographies["Header/H3"]}
  text-align: center;
`;

const StyledFlex = styled.div`
  display: flex;
`;

const StyledIcon = styled.div<{ $backgroundColor: string }>`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 3rem;
  height: 3rem;
  background-color: ${({ $backgroundColor }) =>
    colors[$backgroundColor as keyof typeof colors]};
  border-radius: 3rem;
  & svg {
    width: 1.75rem;
    height: 1.75rem;
    fill: ${colors["colors/text/white"]};
  }
`;
